import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Salesperformance.css";
import FormNotification from "./FormNotification";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";

const API_BASE = "https://cleezoclass.com:8443/api";

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.result)) return value.result;
  return value ? [value] : [];
};

function SalesCharts() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedExecutiveId, setSelectedExecutiveId] = useState("all");
  const [counts, setCounts] = useState({
    leads: 0,
    demos: 0,
    mous: 0,
    centralizations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRecordDetails, setShowRecordDetails] = useState(false);
  const [selectedInstituteHistory, setSelectedInstituteHistory] = useState([]);
  const [schoolHistoryPopupLoading, setSchoolHistoryPopupLoading] = useState(false);
  const [popupContactPerson, setPopupContactPerson] = useState('');
  const [popupContactDesignation, setPopupContactDesignation] = useState('');
  const [popupContactNumber, setPopupContactNumber] = useState('');
  const [popupContactEmail, setPopupContactEmail] = useState('');
  const [popupInstituteAddress, setPopupInstituteAddress] = useState('');

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await axios.get(`${API_BASE}/employee-names`);
        const marketingEmployees = ensureArray(response.data).filter(
          (employee) => employee.designation === "Marketing Executive"
        );
        setEmployees(marketingEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    const loadChartData = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const params =
          selectedExecutiveId === "all" ? {} : { empId: selectedExecutiveId };

        const response = await axios.get(`${API_BASE}/marketing-dashboard-counts`, {
          params,
        });

        setCounts(response.data?.counts || {
          leads: 0,
          demos: 0,
          mous: 0,
          centralizations: 0,
        });
      } catch (error) {
        console.error("Error loading sales chart data:", error);
        setErrorMessage("Unable to load sales chart data.");
        setCounts({
          leads: 0,
          demos: 0,
          mous: 0,
          centralizations: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    loadChartData();
  }, [selectedExecutiveId]);

  const selectedExecutiveName =
    selectedExecutiveId === "all"
      ? "All Marketing Executives"
      : employees.find((item) => String(item.empId) === String(selectedExecutiveId))
        ?.empName || "Selected Executive";

  const data = useMemo(
    () => [
      { name: "Leads", value: counts.leads, fill: "#2563eb" },
      { name: "Demos", value: counts.demos, fill: "#7c3aed" },
      { name: "MOU's", value: counts.mous, fill: "#ef4444" },
      { name: "Centralizations", value: counts.centralizations, fill: "#475569" },
    ],
    [counts]
  );

  const formatTooltipValue = (value) => [`${value}`, "Count"];

  const handleDemoClick = () => {
    window.open('https://cleezoclass.com/book-demo', '_blank', 'noopener,noreferrer');
  };

  const handleMouClick = () => {
    navigate('/MOUProcess');
  };

  const handleCentralisationClick = () => {
    navigate('/Centralisation');
  };

  const handleNotificationInstituteClick = async (item) => {
    if (!item?.institutename) return;

    setShowRecordDetails(true);
    setSchoolHistoryPopupLoading(true);

    try {
      const instituteName = item.institutename.trim();

      const [historyResponse, detailsResponse] = await Promise.all([
        axios.get(`${API_BASE}/school-history/${encodeURIComponent(instituteName)}`),
        axios.get(`${API_BASE}/sales-performance/${encodeURIComponent(instituteName)}`),
      ]);

      const normalizedHistory = Array.isArray(historyResponse.data)
        ? historyResponse.data
            .map((record) => ({
              ...record,
              date: record.date || record.followupdate || record.startdate || record.lastfollowupdate || null,
              comment: record.comment || '-',
              interestshown: record.interestshown || '-',
            }))
            .sort(
              (a, b) =>
                new Date(b.date || b.updated_at || 0) - new Date(a.date || a.updated_at || 0)
            )
        : [];

      setSelectedInstituteHistory(
        normalizedHistory.length > 0
          ? normalizedHistory
          : [
              {
                date: item.date || item.followupdate || item.lastfollowupdate || new Date().toISOString(),
                institutename: item.institutename,
                comment: item.comment || '-',
                interestshown: item.interestshown || '-',
              },
            ]
      );

      setPopupContactPerson(detailsResponse.data.contactperson || '-');
      setPopupContactDesignation(detailsResponse.data.contactpersondesignation || '-');
      setPopupContactNumber(detailsResponse.data.contactpersonnumber || '-');
      setPopupContactEmail(detailsResponse.data.contactpersonemail || '');
      setPopupInstituteAddress(detailsResponse.data.instituteaddress || '-');
    } catch (error) {
      console.error("Error loading sales chart institute history:", error);
      setSelectedInstituteHistory([
        {
          date: item.date || item.followupdate || item.lastfollowupdate || new Date().toISOString(),
          institutename: item.institutename,
          comment: item.comment || '-',
          interestshown: item.interestshown || '-',
        },
      ]);
      setPopupContactPerson('-');
      setPopupContactDesignation('-');
      setPopupContactNumber('-');
      setPopupContactEmail('');
      setPopupInstituteAddress('-');
    } finally {
      setSchoolHistoryPopupLoading(false);
    }
  };

  return (
    <div className="sales-performance-graph-container">
      <FormNotification onInstituteClick={handleNotificationInstituteClick} />
      <h2 className="sales-performance-page-header">Sales Performance</h2>

      <div className="sales-chart-card">
        <div className="marketing-dashboard-chart-header marketing-dashboard-chart-header-row sales-chart-header-row">
          <div>
            <h3>{selectedExecutiveName}</h3>
            <p>Leads, demos, MOU's and centralizations for the selected employee.</p>
          </div>
          <label className="marketing-dashboard-select marketing-dashboard-select-inline">
            <span>Sales Executive</span>
            <select
              value={selectedExecutiveId}
              onChange={(e) => setSelectedExecutiveId(e.target.value)}
              disabled={loading || employees.length === 0}
            >
              <option value="all">All Marketing Executives</option>
              {employees.map((employee) => (
                <option key={employee.empId} value={employee.empId}>
                  {employee.empName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading && <p>Loading chart data...</p>}
        {errorMessage && <p className="sales-performance-error-message">{errorMessage}</p>}
        <ResponsiveContainer width="100%" height={window.innerWidth <= 425 ? 300 : 430}>
          <BarChart
            data={data}
            margin={{ top: 25, right: 25, left: 5, bottom: 20 }}
            barCategoryGap="28%"
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{
                fill: "#334155",
                fontSize: window.innerWidth <= 425 ? 10 : 14,
                fontWeight: 600
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#64748b",
                fontSize: window.innerWidth <= 425 ? 9 : 12
              }}
              allowDecimals={false}
            />
            <Tooltip
              formatter={formatTooltipValue}
              cursor={{ fill: "rgba(90, 116, 136, 0.08)" }}
            />
            <Legend />
            <Bar dataKey="value" name="Count" radius={[6, 6, 0, 0]}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
              <LabelList dataKey="value" position="top" fill="#334155" style={{ fontSize: window.innerWidth <= 425 ? "10px" : "14px" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showRecordDetails && (
        <div className="sales-modal-overlay" onClick={() => setShowRecordDetails(false)}>
          <div
            className="sales-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sales-history-top-row">
              <div className="sales-history-contact">
                <div className="sales-history-contact-name">{popupContactPerson || '-'}</div>
                <div className="sales-history-contact-number">{popupContactNumber || '-'}</div>
              </div>
              <div className="sales-history-title-wrap">
                <h3 className="sales-history-title">
                  History - {selectedInstituteHistory?.[0]?.institutename || '-'}
                </h3>
                <div className="sales-history-address">{popupInstituteAddress || '-'}</div>
              </div>
              <div className="sales-history-comments">
                <div className="sales-history-comments-title">Last Comments</div>
                <ul className="sales-history-comments-list">
                  {selectedInstituteHistory.slice(0, 3).map((record, index) => (
                    <li key={`chart-comment-${index}`}>{record.comment || '-'}</li>
                  ))}
                </ul>
              </div>
            </div>

            {schoolHistoryPopupLoading ? (
              <p className="sales-history-loading">Loading school history...</p>
            ) : (
              <table className="sales-performance-table sales-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Comment</th>
                    <th>Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInstituteHistory.length > 0 ? (
                    selectedInstituteHistory.map((record, index) => (
                      <tr key={`chart-history-${index}`}>
                        <td>{record.date ? new Date(record.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                        <td>{record.comment || '-'}</td>
                        <td>{record.interestshown || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">No history found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            <div className="sales-history-actions">
              <button type="button" onClick={() => setShowRecordDetails(false)}>Close</button>
              <button type="button" onClick={handleDemoClick}>Demo</button>
              <button type="button" onClick={handleMouClick}>MOU</button>
              <button type="button" onClick={handleCentralisationClick}>Centralisation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesCharts;
